<template>
  <div
    :class="[classes(n(`--content--item-container`))]"
    v-bind="ariaAttrs"
    @click="handleClick"
    @touchstart="handleClick"
  >
    <template v-if="key === KeyboardKeyType.Delete">
      <KeyboardDeleteIcon
        aria-hidden="true"
        width="48"
        height="48"
      />
    </template>
    <template v-else-if="key === KeyboardKeyType.Blank" />
    <template v-else-if="key === KeyboardKeyType.Confirm">
      {{ confirmBtnText }}
    </template>
    <template v-else>
      {{ key }}
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'virtual-vue';
import { createNamespace } from '../../utils/components';
import { KeyboardKeyType } from '../keyboard-types';
import { keyProps as props } from '../props';
import { KeyboardDeleteIcon } from '@tencent/wphk-icon-vue';

const { name, n, classes } = createNamespace('keyboard');

export default defineComponent({
  name: `${name}-key`,
  components: { KeyboardDeleteIcon },
  props,
  emits: ['click'],
  setup(props, { emit }) {
    const key = computed(() => props.value);
    // 系统是否支持触摸事件
    const isSupportTouchEvent = ref(false);

    // 无障碍相关处理
    const ariaAttrs = computed(() => {
      if (key.value === KeyboardKeyType.Blank) {
        return { 'aria-hidden': 'true' };
      }
      // 安全模式下对按键播报进行混淆
      if (props.safeMode) {
        return { 'aria-label': 'keyboard' };
      }
      if (key.value === KeyboardKeyType.Delete) {
        return { 'aria-label': 'delete' };
      }
      return {};
    });

    /**
    * 点击键盘键
    * @param key 键盘键值
    * @param event 事件对象
    */
    const handleClick = (event: Event) => {
      event.stopPropagation();
      const isTouchStartEvent = event.type === 'touchstart';
      // 如果支持触摸事件，只触发touchstart事件而不触发click事件，避免重复执行
      if (isSupportTouchEvent.value && !isTouchStartEvent) {
        return;
      }
      // 当发现是touchstart事件时，说明系统支持触摸事件，此时全局设置支持触摸事件标记
      if (!isSupportTouchEvent.value && isTouchStartEvent) {
        isSupportTouchEvent.value = true;
      }
      switch (key.value) {
        case KeyboardKeyType.Delete:
          emit('click', { type: 'delete' });
          break;
        case KeyboardKeyType.Confirm:
          if (props.confirmBtnEnable) {
            emit('click', { type: 'confirm' });
          }
          break;
        case KeyboardKeyType.Blank:
          break;
        default:
          emit('click', { type: 'input', value: key.value });
          break;
      }
    };

    return {
      n,
      classes,
      key,
      KeyboardKeyType,
      ariaAttrs,
      handleClick,
    };
  },
});
</script>
